import * as React from "react";

import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  Group,
  MantineProvider,
  Paper,
  Stack,
  Transition,
} from "@mantine/core";
import { useClickOutside, useHover, useMergedRef } from "@mantine/hooks";
import { IconChecklist, IconPageBreak, IconTrash, IconX, IconPlus } from "@tabler/icons-react";
import {
  BeforeCapture,
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { useDispatch } from "react-redux";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppState } from "../../hooks/useAppState";
import { useCountEvent } from "../../hooks/useCountEvent";
import { useCurrentEditorDocument } from "../../hooks/useEditorState";
import { newChecklist } from "../../lib/generators/checklist";
import { newId } from "../../lib/id/id";
import {
  Checklist,
  ChecklistDocumentElement,
  EditableString,
  isPageBreak,
  pageBreak,
  PageBreak,
} from "../../lib/model/checklistAppState";
import { editorEntry } from "../../lib/state/editor";
import {
  autofocusFieldSlice,
  draggingIdSlice,
  librarySlice,
  isDragAndDropDraggingSlice,
} from "../../lib/state/reducer";

import { ChecklistEditor } from "./ChecklistEditor";
import { EditorControls } from "./EditorControls";
import { InlineEditableTitle } from "./text/InlineEditableText";

export const RealisticEditor = (): JSX.Element => {
  const reduxDispatch = useDispatch();
  const countEvent = useCountEvent();

  const handleReorder = React.useCallback(
    (result: DropResult) => {
      // 1. Clean up DOM compensation (spacer, minHeight, collapsed content)
      const spacer = document.getElementById("dnd-compensation-spacer");
      if (spacer) spacer.remove();

      const container = document.querySelector(
        '[data-rbd-droppable-id="checklistDocument"]'
      );
      if (container instanceof HTMLElement) {
        container.style.minHeight = "";
        // Restore all collapsed checklist content areas
        const allDroppables = container.querySelectorAll("[data-rbd-droppable-id]");
        allDroppables.forEach((droppableEl) => {
          const droppableId = droppableEl.getAttribute("data-rbd-droppable-id");
          if (droppableId && droppableId !== "checklistDocument") {
            const htmlEl = droppableEl as HTMLElement;
            htmlEl.style.height = "";
            htmlEl.style.overflow = "";
          }
        });
      }

      // 2. Dispatch state changes
      reduxDispatch(librarySlice.actions.reorder(result));
      reduxDispatch(draggingIdSlice.actions.setDraggingId(null));
      reduxDispatch(isDragAndDropDraggingSlice.actions.setDndDragging(false));
      countEvent("checklist-reorder");
    },
    [reduxDispatch, countEvent]
  );

  const handleBeforeDragCapture = React.useCallback(
    (before: BeforeCapture) => {
      const el = document.querySelector(
        `[data-rbd-draggable-id="${CSS.escape(before.draggableId)}"]`
      );
      const container = document.querySelector(
        '[data-rbd-droppable-id="checklistDocument"]'
      );
      if (!el || !(container instanceof HTMLElement)) return;

      // 1. Measure element position and container height BEFORE collapse.
      const topBefore = el.getBoundingClientRect().top;
      const containerHeight = container.getBoundingClientRect().height;

      // Pre-set minHeight so the container can't shrink below its current
      // height (prevents scroll clamping when content collapses).
      container.style.minHeight = `${containerHeight}px`;

      // 2. Collapse all checklist content areas via direct DOM manipulation.
      //    This is fully synchronous — no React re-render needed — so rbd
      //    will capture the collapsed layout when onBeforeCapture returns.
      const allDroppables = container.querySelectorAll("[data-rbd-droppable-id]");
      allDroppables.forEach((droppableEl) => {
        const droppableId = droppableEl.getAttribute("data-rbd-droppable-id");
        if (droppableId && droppableId !== "checklistDocument") {
          const htmlEl = droppableEl as HTMLElement;
          htmlEl.style.height = "0px";
          htmlEl.style.overflow = "hidden";
        }
      });

      // 3. Measure how far the element shifted upward and compensate.
      const topAfter = el.getBoundingClientRect().top;
      const shift = topBefore - topAfter;

      if (shift > 0) {
        const spacer = document.createElement("div");
        spacer.id = "dnd-compensation-spacer";
        spacer.style.height = `${shift}px`;
        spacer.style.flexShrink = "0";
        container.insertBefore(spacer, container.firstChild);

        // The Stack's CSS gap adds extra space between the spacer and the
        // first real child. Adjust the spacer height to compensate exactly.
        const topVerify = el.getBoundingClientRect().top;
        const overshoot = topVerify - topBefore;
        if (Math.abs(overshoot) > 1) {
          spacer.style.height = `${Math.max(0, shift - overshoot)}px`;
        }
      }

      // Do NOT dispatch isDragAndDropDragging here. The visual collapse
      // is already handled by DOM manipulation above. Dispatching would
      // cause an async React re-render that adds margin:"10px 0" to each
      // checklist Paper, shifting them after rbd has captured dimensions.
    },
    []
  );

  return (
    <>
      <DragDropContext onBeforeCapture={handleBeforeDragCapture} onDragEnd={handleReorder}>
        <Stack spacing={4} mt={4} mr={8}>
          <EditorControls />
          <ChecklistDocumentEditor />
        </Stack>
      </DragDropContext>
    </>
  );
};

const ChecklistDocumentEditor = (): JSX.Element | null => {
  const reduxDispatch = useAppDispatch();
  const countEvent = useCountEvent();

  // const checklists = useReduxEditorState((state) => state.editor.present.document.checklists);
  // const x = checklists.map((c) => c.id);
  // console.log(x);
  // console.log(x.join(","));
  const ckl = useCurrentEditorDocument(
    (state) => state.contents.checklists.map((c) => c.id).join(","),
    ""
  );
  const checklists = ckl ? ckl.split(",") : [];

  const checklistDocumentTitle = useCurrentEditorDocument((state) => state.contents.title, null);
  // Gate on isDragAndDropDragging so collapse only happens in onBeforeCapture
  // (not on mousedown via interceptDragStart). This lets us measure the
  // pre-collapse position in onBeforeCapture before triggering the collapse.
  const isDraggingChecklist = useAppState((state) => {
    if (!state.isDragAndDropDragging) {
      return false;
    }
    const editingEntry = editorEntry(state.library);

    if (!editingEntry) {
      return false;
    }
    return editingEntry.present.contents.checklists
      .map((c) => c.id)
      .includes(state.draggingId ?? "");
  });

  const documentFontFamily = "Inter, Helvetica, Arial";

  // TODO: degrade more gracefully here
  if (!checklistDocumentTitle) {
    return null;
  }

  return (
    <MantineProvider inherit theme={{ fontFamily: documentFontFamily }}>
      <Paper p="md" withBorder radius="sm">
        <Droppable droppableId="checklistDocument" type="checklist">
          {(provided, _snapshot) => (
            <Stack {...provided.droppableProps} ref={provided.innerRef}>
              <ChecklistDocumentTitle documentTitle={checklistDocumentTitle} />
              {checklists.map((checklistId, idx) => {
                return (
                  <React.Fragment key={checklistId}>
                    <InlineAdd
                      onAdd={(element) => {
                        reduxDispatch(
                          librarySlice.actions.insertChecklistDocumentElement({
                            element,
                            position: idx,
                          })
                        );
                        countEvent(`inline-added-${element.type}`);
                      }}
                    />
                    <ChecklistDocumentElementEditor
                      key={checklistId}
                      idx={idx}
                      renderAnonymous={isDraggingChecklist}
                    />
                  </React.Fragment>
                );
              })}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
        <Center style={{ paddingTop: "20px" }}>
          <Group>
            <AddChecklistButton
              onAdd={(element) => {
                // TODO
                reduxDispatch(librarySlice.actions.appendChecklist(element));
                countEvent(`bottom-added-${element.type}`);
              }}
            />
            <AddPageBreakButton
              onAdd={(_element) => {
                // TODO: decide who makes the element
                reduxDispatch(
                  librarySlice.actions.appendPageBreak({ type: pageBreak, id: newId() })
                );
                countEvent(`bottom-added-pagebreak`);
              }}
            />
          </Group>
        </Center>
      </Paper>
    </MantineProvider>
  );
};

const ChecklistDocumentTitle = ({
  documentTitle,
}: {
  documentTitle: EditableString;
}): JSX.Element => {
  const reduxDispatch = useAppDispatch();
  const numChecklists = useCurrentEditorDocument((state) => state.contents.checklists.length, 0);
  const countEvent = useCountEvent();

  const addNewChecklistIfEmpty = React.useCallback(() => {
    if (numChecklists === 0) {
      const autoChecklist = newChecklist();
      reduxDispatch(librarySlice.actions.appendChecklist(autoChecklist));
      reduxDispatch(autofocusFieldSlice.actions.setAutofocusField(autoChecklist.title.id));
    }
  }, [reduxDispatch, numChecklists]);

  return (
    <Box style={{ backgroundColor: "#000" }}>
      <Center>
        <InlineEditableTitle
          text={documentTitle}
          color="#fff"
          placeholder="Document Title"
          order={1}
          extraPadding={20}
          justify="center"
          onSave={(newValue) => {
            reduxDispatch(librarySlice.actions.setDocumentTitle(newValue));
            countEvent("document-title-edited");
          }}
          onEmptyEnter={addNewChecklistIfEmpty}
          onEmptyTab={addNewChecklistIfEmpty}
        />
      </Center>
    </Box>
  );
};

const ChecklistDocumentElementEditor = ({
  idx,
  renderAnonymous,
}: {
  idx: number;
  renderAnonymous: boolean;
}): JSX.Element | null => {
  const reduxDispatch = useAppDispatch();
  const checklistElement = useCurrentEditorDocument(
    (state) => state.contents.checklists[idx],
    null
  );

  if (!checklistElement) {
    return null;
  }

  const onRemove = React.useCallback(() => {
    // TODO: state management
    reduxDispatch(librarySlice.actions.removeChecklist(checklistElement.id));
  }, [reduxDispatch, checklistElement]);

  if (isPageBreak(checklistElement)) {
    return <PageBreakDivider id={checklistElement.id} index={idx} onRemove={onRemove} />;
  } else {
    return <ChecklistEditor index={idx} renderAnonymous={renderAnonymous} onRemove={onRemove} />;
  }
};

const AddChecklistButton = ({ onAdd }: { onAdd: (element: Checklist) => void }): JSX.Element => {
  const reduxDispatch = useAppDispatch();
  return (
    <Button
      leftIcon={<IconChecklist size={16} />}
      color="gray"
      variant="filled"
      size="xs"
      radius="xl"
      compact
      styles={{
        leftIcon: {
          marginRight: 5,
        },
      }}
      onClick={() => {
        const generatedChecklist = newChecklist();
        onAdd(generatedChecklist);
        reduxDispatch(autofocusFieldSlice.actions.setAutofocusField(generatedChecklist.title.id));
      }}
    >
      New Checklist
    </Button>
  );
};

const AddPageBreakButton = ({ onAdd }: { onAdd: (element: PageBreak) => void }): JSX.Element => {
  return (
    <Button
      leftIcon={<IconPageBreak size={16} />}
      color="gray"
      variant="filled"
      size="xs"
      radius="xl"
      compact
      styles={{
        leftIcon: {
          marginRight: 5,
        },
      }}
      onClick={() => {
        const generatedPageBreak: PageBreak = { id: newId(), type: pageBreak };
        onAdd(generatedPageBreak);
      }}
    >
      Insert Page Break
    </Button>
  );
};

const PageBreakDivider = ({
  id,
  index,
  onRemove,
}: {
  id: string;
  index: number;
  onRemove: () => void;
}): JSX.Element => {
  const isDndDragging = useAppState((state) => state.isDragAndDropDragging);
  const reduxDispatch = useAppDispatch();
  const interceptDragStart = React.useCallback(() => {
    reduxDispatch(draggingIdSlice.actions.setDraggingId(id));
  }, [reduxDispatch, id]);
  const resetDrag = React.useCallback(() => {
    if (isDndDragging) {
      reduxDispatch(draggingIdSlice.actions.setDraggingId(null));
    }
  }, [reduxDispatch, isDndDragging]);

  return (
    <Draggable draggableId={id} index={index}>
      {(draggableProvided, _draggableSnapshot) => (
        <Divider
          variant="dashed"
          size="sm"
          onMouseDown={interceptDragStart}
          onMouseUp={resetDrag}
          onTouchStart={interceptDragStart}
          onTouchEnd={resetDrag}
          label={
            <Group style={{ zIndex: 100 }} spacing={2}>
              <>Page Break</>
              <ActionIcon
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  e.stopPropagation();
                }}
                onMouseUp={resetDrag}
                onTouchStart={(e: React.TouchEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                }}
                onTouchEnd={resetDrag}
                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  onRemove();
                  e.preventDefault();
                  e.stopPropagation();
                  reduxDispatch(draggingIdSlice.actions.setDraggingId(null));
                }}
                size={"sm"}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          }
          my={-5}
          {...draggableProvided.draggableProps}
          {...draggableProvided.dragHandleProps}
          ref={draggableProvided.innerRef}
          labelPosition="center"
        />
      )}
    </Draggable>
  );
};

const InlineAdd = ({
  onAdd,
  disabled,
}: {
  onAdd: (element: ChecklistDocumentElement) => void;
  disabled?: boolean;
}): JSX.Element => {
  const { hovered, ref: hoverRef } = useHover();
  const [open, setOpen] = React.useState<boolean>(false);

  const clickOutsideRef = useClickOutside(() => setOpen(false));

  const showing = (hovered || open) && !disabled;

  const openCloseButton = React.useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const ref = useMergedRef(hoverRef, clickOutsideRef);

  return (
    <Box ref={ref} my={-12} ml={-10} style={{ zIndex: 10 }}>
      <Divider
        label={
          <Group>
            <ActionIcon
              size={"xs"}
              radius="xl"
              variant="filled"
              color={open ? "gray" : "green"}
              onClick={openCloseButton}
              style={{ zIndex: 20 }}
            >
              {open ? <IconX /> : <IconPlus />}
            </ActionIcon>
            <Transition
              mounted={open}
              transition="slide-right"
              duration={100}
              exitDuration={100}
              timingFunction="ease"
            >
              {(_styles) => (
                <Group>
                  <AddChecklistButton onAdd={onAdd} />
                  <AddPageBreakButton onAdd={onAdd} />
                </Group>
              )}
            </Transition>
          </Group>
        }
        style={{
          opacity: showing ? 1 : 0,
          zIndex: 1,
        }}
        labelPosition="left"
        my={-3}
        py={-2}
      />
    </Box>
  );
};
